/**
 * Pure view-model helpers for the Aion-style unit frames (target, target-of-target, party) and
 * nameplates. No DOM here, so everything is unit-testable. Per-frame helpers write into
 * caller-owned arrays and return interned strings (no allocation once warm).
 */
import type { AuraDef, AuraInstance, ClassDef, ContentRegistry, DeepReadonly, DispelType, NpcTier, Unit, UnitId, World } from '@keystone/sim';

type RO<T> = DeepReadonly<T>;
type Inst = RO<AuraInstance>;

// ---------------------------------------------------------------------------------------------
// Aura ordering
// ---------------------------------------------------------------------------------------------

export type AuraBucket = 'buff' | 'debuff';

/** Sort context (module-level so the comparator is one function, created once). */
const sortCtx: { playerId: UnitId | null; now: number; content: ContentRegistry | null; dispel: ReadonlySet<DispelType> | null } = {
  playerId: null,
  now: 0,
  content: null,
  dispel: null,
};

function remaining(a: Inst, now: number): number {
  return a.expiresAt === null ? Infinity : Math.max(0, a.expiresAt - now);
}

/** Lower rank shows first. */
function rank(a: Inst): number {
  const def = sortCtx.content!.aura(a.auraId);
  let r = 0;
  // Things the viewer can act on come first: debuffs they can dispel, then their own auras, then CC.
  if (sortCtx.dispel && def?.dispelType && def.kind === 'debuff' && sortCtx.dispel.has(def.dispelType)) r -= 8;
  if (sortCtx.playerId !== null && a.casterId === sortCtx.playerId) r -= 4;
  if (def?.cc) r -= 2;
  return r;
}

function compare(x: Inst, y: Inst): number {
  const d = rank(x) - rank(y);
  if (d !== 0) return d;
  const rx = remaining(x, sortCtx.now);
  const ry = remaining(y, sortCtx.now);
  if (rx !== ry) return rx === Infinity ? 1 : ry === Infinity ? -1 : rx - ry;
  // Stable: older application first.
  return x.appliedAt - y.appliedAt;
}

export interface OrderAurasOptions {
  /** Buffs or debuffs. */
  kind: AuraBucket;
  /** The viewer (their own auras sort first). */
  playerId: UnitId | null;
  /** Dispel types the viewer can remove (dispellable debuffs sort first). */
  dispel?: ReadonlySet<DispelType> | null;
  /** Only the viewer's auras plus crowd control (nameplate style). */
  mineOnly?: boolean;
  max: number;
}

/**
 * Fill `out` with the unit's visible auras of one kind, most relevant first:
 * dispellable-by-me > mine > crowd control > others, then soonest to expire (permanent last),
 * then oldest. Hidden auras are skipped. Returns the count (= out.length). Reuses `out`.
 */
export function orderAuras(auras: readonly Inst[], content: ContentRegistry, now: number, opts: OrderAurasOptions, out: Inst[]): number {
  out.length = 0;
  for (let i = 0; i < auras.length; i++) {
    const a = auras[i]!;
    const def = content.aura(a.auraId);
    if (!def || def.flags?.hidden || def.kind !== opts.kind) continue;
    if (opts.mineOnly && !def.cc && a.casterId !== opts.playerId) continue;
    out.push(a);
  }
  sortCtx.playerId = opts.playerId;
  sortCtx.now = now;
  sortCtx.content = content;
  sortCtx.dispel = opts.dispel ?? null;
  if (out.length > 1) out.sort(compare);
  sortCtx.content = null;
  sortCtx.dispel = null;
  if (out.length > opts.max) out.length = opts.max;
  return out.length;
}

// ---------------------------------------------------------------------------------------------
// Dispels
// ---------------------------------------------------------------------------------------------

const dispelCache = new WeakMap<object, ReadonlySet<DispelType>>();
const NO_DISPEL: ReadonlySet<DispelType> = new Set();

function walkDispels(node: unknown, out: Set<DispelType>, depth: number): void {
  if (!node || typeof node !== 'object' || depth > 8) return;
  if (Array.isArray(node)) {
    for (const n of node) walkDispels(n, out, depth + 1);
    return;
  }
  const o = node as Record<string, unknown>;
  if (o['type'] === 'dispel' && o['harmful'] !== false && Array.isArray(o['dispelTypes'])) {
    for (const t of o['dispelTypes'] as DispelType[]) out.add(t);
  }
  for (const k in o) if (k !== 'dispelTypes') walkDispels(o[k], out, depth + 1);
}

/** Debuff types a class can remove from allies (scans its abilities' effects; cached per class). */
export function dispelTypesFor(cls: RO<ClassDef> | undefined | null): ReadonlySet<DispelType> {
  if (!cls) return NO_DISPEL;
  let s = dispelCache.get(cls);
  if (!s) {
    const out = new Set<DispelType>();
    walkDispels(cls.abilities, out, 0);
    s = out.size ? out : NO_DISPEL;
    dispelCache.set(cls, s);
  }
  return s;
}

/** The first debuff on `u` the viewer can dispel (its type), or null. */
export function dispellableType(u: RO<Unit>, content: ContentRegistry, dispel: ReadonlySet<DispelType>): DispelType | null {
  if (dispel.size === 0) return null;
  for (const a of u.auras) {
    const def: RO<AuraDef> | undefined = content.aura(a.auraId);
    if (def?.kind === 'debuff' && def.dispelType && !def.flags?.hidden && dispel.has(def.dispelType)) return def.dispelType;
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Threat
// ---------------------------------------------------------------------------------------------

/**
 * Nameplate threat colour state for an enemy:
 * - 'none'   out of combat / no victim
 * - 'tanked' on a tank (normal red)
 * - 'aggro'  on the viewer, and the viewer is not a tank (pull it off you!)
 * - 'loose'  on some other non-tank party member (tank: taunt it)
 */
export type ThreatState = 'none' | 'tanked' | 'aggro' | 'loose';

export function threatState(u: RO<Unit> | undefined, world: RO<World>, playerId: UnitId | null): ThreatState {
  if (!u || !u.alive || !u.inCombat || u.faction !== 'enemy') return 'none';
  let victim = u.threat?.victimId ?? null;
  if (!victim && u.targetId && world.units[u.targetId]?.faction === 'party') victim = u.targetId;
  if (!victim) return 'none';
  const v = world.units[victim];
  if (!v || v.faction !== 'party') return 'none';
  if (v.role === 'tank') return 'tanked';
  if (victim === playerId) return 'aggro';
  return 'loose';
}

// ---------------------------------------------------------------------------------------------
// Badges and text
// ---------------------------------------------------------------------------------------------

export const TIER_LABEL: Readonly<Record<NpcTier, string>> = {
  fodder: 'Minion',
  standard: '',
  caster: 'Caster',
  big: 'Elite',
  miniboss: 'Elite',
  boss: 'Boss',
};

export interface UnitBadge {
  /** Short text in the level badge ("+12", "Lv.20"). Empty hides the badge. */
  level: string;
  /** Rank label under / beside the name ("Boss", "Elite", class name). */
  rank: string;
  /** CSS modifier: 'boss' | 'elite' | 'normal' | 'friendly'. */
  tone: 'boss' | 'elite' | 'normal' | 'friendly';
}

/** Level/tier badge for a unit (call on unit / key level change, not per frame). */
export function unitBadge(u: RO<Unit>, world: RO<World>, content: ContentRegistry): UnitBadge {
  if (u.faction === 'enemy') {
    const tier = u.tier ?? 'standard';
    const lvl = world.mplus?.level ?? 0;
    return {
      level: lvl > 0 ? `+${lvl}` : '',
      rank: TIER_LABEL[tier],
      tone: tier === 'boss' ? 'boss' : tier === 'miniboss' || tier === 'big' ? 'elite' : 'normal',
    };
  }
  const cls = u.classId ? content.class(u.classId) : undefined;
  const role = u.role ? u.role[0]!.toUpperCase() + u.role.slice(1) : '';
  return { level: '', rank: cls?.name ?? role, tone: 'friendly' };
}

/** PCT[i] = "<i>%" for 0..100 (interned). */
export const PCT: readonly string[] = Array.from({ length: 101 }, (_, i) => `${i}%`);

/** Whole-percent HP (ceil so a living unit never reads 0%). */
export function hpPercent(hp: number, max: number): number {
  if (!(max > 0) || !(hp > 0)) return 0;
  return Math.min(100, Math.max(1, Math.ceil((hp / max) * 100 - 1e-9)));
}

/** Party frame range check: beyond this edge-to-edge distance the frame fades (WoW's 40 yd). */
export const FRAME_RANGE = 40;

/** Class colour for a unit's name (party / player), else the faction tint. */
export function nameColor(u: RO<Unit>, content: ContentRegistry): string {
  if (u.faction === 'enemy') return '#ffd2cc';
  const cls = u.classId ? content.class(u.classId) : undefined;
  return cls?.color ?? '#e6e9ef';
}
